import React from "react";
import { motion } from "framer-motion";

interface TimelineDotProps {
    isActive: boolean;
    index: number;
    size?: 'small' | 'medium' | 'large';
    activeColor?: string;
    inactiveColor?: string;
    borderColor?: string;
    showTrail?: boolean;
    trailProgress?: number;
    className?: string;
}

export const TimelineDot: React.FC<TimelineDotProps> = ({
    isActive,
    index,
    size = 'medium',
    activeColor = '#899B30',
    inactiveColor = '#ffffff',
    borderColor = '#E3DBC8',
    showTrail = false,
    trailProgress = 0,
    className = ''
}) => {
    const sizeClasses = {
        small: 'w-[10px] h-[10px]',
        medium: 'w-[14px] h-[14px]',
        large: 'w-[18px] h-[18px]'
    };

    const dotSize = sizeClasses[size];

    return (
        <div className={`timeline-dot-container flex-shrink-0 w-12 flex justify-center relative ${className}`}>
            {/* Trailing effect */}
            {showTrail && trailProgress > 0 && (
                <motion.div
                    className="absolute top-0 left-1/2 transform -translate-x-1/2 w-[2px] bg-gradient-to-b from-transparent via-current to-transparent"
                    style={{
                        height: `${Math.max(0, Math.min(100, trailProgress))}px`,
                        color: activeColor,
                        opacity: 0.6,
                        willChange: 'height',
                        transform: 'translateZ(0) translateX(-50%)'
                    }}
                    initial={{ height: 0 }}
                    animate={{ height: `${Math.max(0, Math.min(100, trailProgress))}px` }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                />
            )}
            
            {/* Main dot */}
            <motion.div 
                className={`timeline-dot ${dotSize} rounded-full border-2 transition-all duration-200 relative z-10`}
                style={{
                    backgroundColor: isActive ? activeColor : inactiveColor,
                    borderColor: isActive ? activeColor : borderColor,
                    willChange: 'transform, opacity',
                    transform: 'translateZ(0)'
                }}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ 
                    scale: isActive ? 1.1 : 1, 
                    opacity: 1,
                    boxShadow: isActive ? `0 0 0 4px ${activeColor}20` : 'none'
                }}
                transition={{ 
                    duration: 0.2, 
                    delay: index * 0.03,
                    type: "spring",
                    stiffness: 300,
                    damping: 25
                }}
                whileHover={{ scale: isActive ? 1.2 : 1.05 }}
            >
                {/* Inner pulse effect for active state */}
                {isActive && (
                    <motion.div
                        className="absolute inset-0 rounded-full"
                        style={{ 
                            backgroundColor: activeColor,
                            willChange: 'transform',
                            transform: 'translateZ(0)'
                        }}
                        initial={{ scale: 0 }}
                        animate={{ scale: [0, 1.3, 0] }}
                        transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                    />
                )}
            </motion.div>
        </div>
    );
};

export default TimelineDot;
